import { useEffect } from "react";
import {
  App,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  TimePicker,
  Typography,
} from "antd";
import dayjs from "dayjs";
import {
  BotTaskInterface,
  BotTaskType,
  CreateBotTaskPayload,
  useCreateBotTaskMutation,
  useGetCategoriesQuery,
  useUpdateBotTaskMutation,
} from "api";
import { getErrorMessage } from "components";

interface AddTaskModalProps {
  open: boolean;
  botId: number;
  /** When provided → edit mode; otherwise → create. */
  task?: BotTaskInterface;
  /**
   * Task types already configured on this bot — disabled in the picker so
   * the admin can't create a duplicate (server enforces this too with a 422,
   * UI just hides the doomed option). Empty in edit mode (the type field is
   * locked anyway) or for fresh bots.
   */
  usedTaskTypes?: BotTaskType[];
  onCancel: () => void;
  onSaved?: () => void;
}

const TASK_TYPE_OPTIONS: { value: BotTaskType; label: string }[] = [
  { value: BotTaskType.CreateFreePost, label: "Creeaza postari (free)" },
  { value: BotTaskType.LikeFeed, label: "Apreciaza postari din feed" },
  { value: BotTaskType.CommentFeed, label: "Comenteaza postari din feed" },
  { value: BotTaskType.ViewFeed, label: "Vizualizeaza feed (impresii)" },
  { value: BotTaskType.ActivityPing, label: "Mentine activitatea (ping)" },
  { value: BotTaskType.ReadNotifications, label: "Citeste notificari" },
  // ---- Social graph ----
  { value: BotTaskType.FollowRelevantUsers, label: "Urmareste utilizatori relevanti" },
  { value: BotTaskType.UnfollowInactiveUsers, label: "Renunta la urmariri inactive" },
  { value: BotTaskType.WelcomeNewUsers, label: "Intampina utilizatori noi" },
  // ---- Reposts / amplification ----
  { value: BotTaskType.RepostFreeFeed, label: "Redistribuie postari populare (repost)" },
  { value: BotTaskType.QuoteRepostFreeFeed, label: "Redistribuie cu comentariu (quote repost)" },
  // ---- Comments / discoverability ----
  { value: BotTaskType.LikeRealComments, label: "Apreciaza comentarii reale" },
  { value: BotTaskType.MentionRelevantUser, label: "Mentioneaza utilizatori in comentarii" },
  // ---- Events ----
  { value: BotTaskType.ReactEventMessageContextual, label: "Reactioneaza la mesaje din eveniment (emoji contextual)" },
];

// Partial: REPORT_VIOLATIONS is intentionally absent (not selectable while the
// bot-reporting ETHICS decision is open). Lookups fall back to "" below.
const TASK_TYPE_DESCRIPTIONS: Partial<Record<BotTaskType, string>> = {
  [BotTaskType.CreateFreePost]:
    "Botul genereaza periodic postari noi (gratuite) in categoriile selectate. Continutul este creat automat cu Gemini.",
  [BotTaskType.LikeFeed]:
    "Botul deschide feed-ul si apreciaza postari ale altor utilizatori. Nu se actioneaza pe postari platite.",
  [BotTaskType.CommentFeed]:
    "Botul lasa comentarii scurte pe postari din feed. Textul comentariului este generat cu Gemini in functie de continutul postarii.",
  [BotTaskType.ViewFeed]:
    "Botul scroll-eaza prin feed si genereaza impresii pe postari. Impresiile URCA contorul public de vizualizari, exact ca la un utilizator real: o singura vizualizare per postare per bot, iar revenirile pe aceeasi postare nu mai umfla contorul. Postarile platite raman complet in afara actiunii botilor, deci metricile pentru advertiseri nu sunt afectate.",
  [BotTaskType.ActivityPing]:
    "Marcheaza periodic botul ca activ in app, fara nicio actiune vizibila. Util pentru a pastra contul ca user activ in metrici.",
  [BotTaskType.ReadNotifications]:
    "Botul deschide periodic lista de notificari si le marcheaza ca citite. Imita comportamentul unui user real.",
  [BotTaskType.FollowRelevantUsers]:
    "Botul cauta utilizatori reali compatibili cu persona sa (interese suprapuse, aceeasi categorie sau judet) si ii urmareste. Fiecare follow nou trimite notificare push + intrare in inbox la utilizatorul tinta (\"X te-a urmarit\") prin UserFollowedYou — il aduce inapoi in app. Contorul de followers creste si profilul botului apare in lista \"Followers\". Targeting prin overlap, NU follow random in masa. Botii nu se urmaresc intre ei.",
  [BotTaskType.UnfollowInactiveUsers]:
    "Igiena pe lista de following: botul renunta la urmarirea conturilor care nu au mai fost active de cel putin N zile. Pastreaza un following_count realist si arata comportament natural. Actioneaza doar pe relatii create chiar de bot.",
  [BotTaskType.WelcomeNewUsers]:
    "Eveniment-driven: cand un utilizator real se inregistreaza, cativa boti relevanti il urmaresc in primele 24h. Fiecare follow declanseaza push + inbox prin UserFollowedYou, deci utilizatorul nou primeste notificari \"X te-a urmarit\" din prima zi — combate sentimentul de \"nimeni nu ma observa\". Quota stricta: max ~3 boti per utilizator nou.",
  [BotTaskType.RepostFreeFeed]:
    "Botul face simple repost (X-style) la postari gratuite populare ale utilizatorilor reali. Mareste reach-ul postarilor bune fara a genera continut nou. Postarile platite sunt excluse complet (ETHICS #2). Botii nu repost intre ei.",
  [BotTaskType.QuoteRepostFreeFeed]:
    "Varianta mai bogata a redistribuirii: botul face quote repost la postari populare, adaugand un comentariu scurt generat AI in tonul personei. Comentariul trece prin AiDetectionService la fel ca un comment obisnuit. Frecventa mica (recomandat 1/zi/bot).",
  [BotTaskType.LikeRealComments]:
    "Botul apreciaza comentariile bune ale utilizatorilor reali. Spre deosebire de \"Apreciaza postari din feed\" (care actioneaza pe postari), aici actioneaza pe layer-ul de discutie. Comentariile altor boti sunt sarite.",
  [BotTaskType.MentionRelevantUser]:
    "Cand botul comenteaza la o postare, include ocazional un @mention catre un utilizator real cu continut similar pe topic. Mention-ul declanseaza notificare push (la fel ca pentru un mention real) si aduce userul inapoi in app. Plafon strict ca sa nu inundam o singura persoana cu notificari.",
  [BotTaskType.ReactEventMessageContextual]:
    "In chat-ul evenimentelor live, botul adauga reactii emoji la mesajele utilizatorilor reali (TEXT, IMAGINI sau VIDEO). Emoji-ul ales este RELEVANT pentru continut, mapat din setul EventService::ALLOWED_REACTIONS (👍 ❤️ 😂 😮 😢 🔥): entuziast → 🔥, amuzant → 😂, pozitiv/dragut → ❤️, surpriza → 😮, trist → 😢, acord → 👍. Worker-ul ruleaza un pas scurt de analiza sentiment/tema pe mesaj inainte de alegere.",
};

const SCHEDULE_DEFAULTS = {
  timezone: "Europe/Bucharest",
  active_hours_start: "08:00",
  active_hours_end: "23:00",
  weekend_modifier: 0.7,
};

const TIME_FORMAT = "HH:mm";

const TIMEZONE_OPTIONS = [
  "Europe/Bucharest",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "UTC",
].map((tz) => ({ value: tz, label: tz }));

export const AddTaskModal = ({
  open,
  botId,
  task,
  usedTaskTypes = [],
  onCancel,
  onSaved,
}: AddTaskModalProps) => {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const isEdit = task !== undefined;

  const taskType = Form.useWatch("task_type", form) as BotTaskType | undefined;

  const { data: categories } = useGetCategoriesQuery({}, { skip: !open });

  const [createTask, { isLoading: isCreating }] = useCreateBotTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateBotTaskMutation();

  // Hydrate form when opening / switching task.
  useEffect(() => {
    if (!open) return;
    if (task) {
      form.setFieldsValue({
        task_type: task.task_type.value,
        config: task.config,
        schedule: {
          ...SCHEDULE_DEFAULTS,
          ...task.schedule,
          active_hours_start: dayjs(
            task.schedule.active_hours_start,
            TIME_FORMAT
          ),
          active_hours_end: dayjs(task.schedule.active_hours_end, TIME_FORMAT),
        },
        priority: task.priority,
        is_enabled: task.is_enabled,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        task_type: BotTaskType.LikeFeed,
        config: defaultsFor(BotTaskType.LikeFeed),
        schedule: {
          ...SCHEDULE_DEFAULTS,
          active_hours_start: dayjs("08:00", TIME_FORMAT),
          active_hours_end: dayjs("23:00", TIME_FORMAT),
        },
        priority: 5,
        is_enabled: true,
      });
    }
  }, [open, task, form]);

  // When task_type changes (in create mode), reset config to that type's defaults.
  useEffect(() => {
    if (!open || isEdit || !taskType) return;
    form.setFieldsValue({ config: defaultsFor(taskType) });
  }, [taskType, open, isEdit, form]);

  const onSubmit = async () => {
    const values = await form.validateFields();
    const payload: CreateBotTaskPayload = {
      bot_id: botId,
      task_type: values.task_type,
      config: values.config,
      schedule: {
        ...SCHEDULE_DEFAULTS,
        timezone: values.schedule?.timezone ?? SCHEDULE_DEFAULTS.timezone,
        active_hours_start: dayjs.isDayjs(values.schedule?.active_hours_start)
          ? values.schedule.active_hours_start.format(TIME_FORMAT)
          : SCHEDULE_DEFAULTS.active_hours_start,
        active_hours_end: dayjs.isDayjs(values.schedule?.active_hours_end)
          ? values.schedule.active_hours_end.format(TIME_FORMAT)
          : SCHEDULE_DEFAULTS.active_hours_end,
        weekend_modifier:
          values.schedule?.weekend_modifier ??
          SCHEDULE_DEFAULTS.weekend_modifier,
      },
      priority: values.priority,
      is_enabled: values.is_enabled,
    };

    try {
      if (isEdit && task) {
        await updateTask({ ...payload, id: task.id }).unwrap();
        notification.success({ message: "Task actualizat." });
      } else {
        await createTask(payload).unwrap();
        notification.success({ message: "Task adaugat." });
      }
      onSaved?.();
      onCancel();
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  return (
    <Modal
      title={isEdit ? "Editeaza task" : "Adauga task"}
      open={open}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Salveaza"
      cancelText="Anuleaza"
      confirmLoading={isCreating || isUpdating}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="task_type"
          label="Tip task"
          rules={[{ required: true }]}
          extra={
            taskType
              ? (TASK_TYPE_DESCRIPTIONS[taskType] ?? "")
              : "Alege ce tip de actiune va executa botul. Tipurile deja configurate sunt dezactivate."
          }
        >
          <Select
            options={TASK_TYPE_OPTIONS.map((opt) => ({
              ...opt,
              // Disable types already used by this bot — only when creating
              // (in edit mode the whole select is disabled and we keep the
              // current type's label visible).
              disabled: !isEdit && usedTaskTypes.includes(opt.value),
              label: usedTaskTypes.includes(opt.value)
                ? `${opt.label} (deja configurat)`
                : opt.label,
            }))}
            disabled={isEdit}
          />
        </Form.Item>

        {taskType && (
          <ConfigFields taskType={taskType} categories={categories || []} />
        )}

        <Typography.Title level={5} style={{ marginTop: 24 }}>
          Programare
        </Typography.Title>
        <Form.Item
          name={["schedule", "timezone"]}
          label="Fus orar"
          extra="Toate orele de mai jos se interpreteaza in acest fus orar. Pentru utilizatorii din Romania pastreaza Europe/Bucharest."
        >
          <Select options={TIMEZONE_OPTIONS} />
        </Form.Item>
        <Space.Compact block>
          <Form.Item
            name={["schedule", "active_hours_start"]}
            label="Ore active - de la"
            style={{ flex: 1, marginRight: 8 }}
            rules={[{ required: true }]}
            extra="Botul nu va actiona inainte de aceasta ora."
          >
            <TimePicker
              format={TIME_FORMAT}
              minuteStep={15}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name={["schedule", "active_hours_end"]}
            label="pana la"
            style={{ flex: 1 }}
            rules={[{ required: true }]}
            extra="Botul nu va actiona dupa aceasta ora."
          >
            <TimePicker
              format={TIME_FORMAT}
              minuteStep={15}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Space.Compact>
        <Form.Item
          name={["schedule", "weekend_modifier"]}
          label="Multiplicator weekend"
          extra="Cat de activ este botul in weekend fata de zilele lucratoare. 1.0 = la fel; 0.7 = cu 30% mai putin; 0 = inactiv in weekend; 1.5 = mai activ in weekend."
        >
          <InputNumber min={0} max={2} step={0.1} style={{ width: "100%" }} />
        </Form.Item>

        <Space.Compact block style={{ marginTop: 16 }}>
          <Form.Item
            name="priority"
            label="Prioritate"
            style={{ flex: 1, marginRight: 8 }}
            extra="1 = cea mai mica, 10 = cea mai mare. Cand mai multe sarcini sunt scadente in acelasi timp, scheduler-ul executa intai pe cele cu prioritate mai mare."
          >
            <InputNumber
              min={1}
              max={10}
              precision={0}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="is_enabled"
            label="Activ"
            style={{ flex: 1 }}
            extra="Daca alegi Nu, sarcina ramane salvata dar nu se executa."
          >
            <Select
              options={[
                { value: true, label: "Da" },
                { value: false, label: "Nu" },
              ]}
            />
          </Form.Item>
        </Space.Compact>
      </Form>
    </Modal>
  );
};

interface ConfigFieldsProps {
  taskType: BotTaskType;
  categories: { id: number; name: string }[];
}

const ConfigFields = ({ taskType, categories }: ConfigFieldsProps) => {
  switch (taskType) {
    case BotTaskType.CreateFreePost:
      return (
        <>
          <Space.Compact block>
            <Form.Item
              name={["config", "posts_per_week_mean"]}
              label="Postari pe saptamana (medie)"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra="Cate postari noi creeaza botul in medie pe saptamana. Ex: 3 = aproximativ 12 postari/luna."
            >
              <InputNumber
                min={0}
                max={50}
                precision={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name={["config", "posts_per_week_stddev"]}
              label="Deviatie"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
              extra="Variatia naturala fata de medie. Ex: medie 3 + deviatie 1 = unele saptamani 2 postari, altele 4."
            >
              <InputNumber
                min={0}
                max={20}
                precision={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name={["config", "category_ids"]}
            label="Categorii in care posteaza"
            rules={[{ required: true }]}
            extra="Subset din categoriile alese la crearea botului. Pentru fiecare postare, se alege random una din aceste categorii."
          >
            <Select
              mode="multiple"
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
              showSearch
              optionFilterProp="label"
              placeholder="Selecteaza una sau mai multe categorii"
            />
          </Form.Item>
        </>
      );

    case BotTaskType.LikeFeed:
    case BotTaskType.CommentFeed:
    case BotTaskType.ViewFeed: {
      const actionLabel =
        taskType === BotTaskType.LikeFeed
          ? "aprecieri"
          : taskType === BotTaskType.CommentFeed
            ? "comentarii"
            : "vizualizari";
      return (
        <>
          <Space.Compact block>
            <Form.Item
              name={["config", "actions_per_day_mean"]}
              label={`${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} pe zi (medie)`}
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra={`Cate ${actionLabel} face botul in medie pe zi.`}
            >
              <InputNumber
                min={0}
                max={500}
                precision={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name={["config", "actions_per_day_stddev"]}
              label="Deviatie"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra="Cat poate varia numarul fata de medie de la o zi la alta."
            >
              <InputNumber
                min={0}
                max={250}
                precision={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name={["config", "max_per_day"]}
              label="Plafon zilnic"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
              extra={`Limita absoluta de ${actionLabel} intr-o zi, indiferent de medie.`}
            >
              <InputNumber
                min={1}
                max={1000}
                precision={0}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Space.Compact>
        </>
      );
    }

    case BotTaskType.ActivityPing:
      return (
        <Space.Compact block>
          <Form.Item
            name={["config", "interval_minutes"]}
            label="Interval intre ping-uri (minute)"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
            extra="Cat de des botul se marcheaza ca activ. 60 = o data pe ora."
          >
            <InputNumber
              min={5}
              max={1440}
              precision={0}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name={["config", "jitter_minutes"]}
            label="Variatie aleatoare (minute)"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
            extra="Pentru a nu rula exact la fix. 15 = ping-ul pica intre 45 si 75 min de la cel anterior."
          >
            <InputNumber
              min={0}
              max={120}
              precision={0}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Space.Compact>
      );

    case BotTaskType.ReadNotifications:
      return (
        <Form.Item
          name={["config", "interval_minutes"]}
          label="Interval citire notificari (minute)"
          rules={[{ required: true }]}
          extra="Cat de des botul deschide centrul de notificari si le marcheaza ca citite. 30 = la fiecare jumatate de ora."
        >
          <InputNumber
            min={5}
            max={1440}
            precision={0}
            style={{ width: "100%" }}
          />
        </Form.Item>
      );

    // ---- Social graph ----
    case BotTaskType.FollowRelevantUsers:
      return (
        <Space.Compact block>
          <Form.Item
            name={["config", "follows_per_day_mean"]}
            label="Follow-uri pe zi (medie)"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
            extra="Numar mediu de utilizatori urmariti zilnic. Recomandat 5-10."
          >
            <InputNumber min={0} max={30} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "follows_per_day_stddev"]}
            label="Deviatie"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
            extra="Variatia naturala fata de medie."
          >
            <InputNumber min={0} max={15} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "max_per_day"]}
            label="Plafon zilnic"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
            extra="Limita absoluta. Backend respinge configuri >30/zi (pattern de spam)."
          >
            <InputNumber min={1} max={30} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Space.Compact>
      );

    case BotTaskType.UnfollowInactiveUsers:
      return (
        <>
          <Space.Compact block>
            <Form.Item
              name={["config", "interval_minutes"]}
              label="Interval rulare (minute)"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra="Cat de des ruleaza pasul de curatenie. Recomandat 720 (12h) sau mai mult."
            >
              <InputNumber min={60} max={10080} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name={["config", "max_per_day"]}
              label="Maxim unfollows/zi"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
              extra="Plafon zilnic pentru curatenie. Recomandat 5-10."
            >
              <InputNumber min={1} max={50} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name={["config", "inactive_threshold_days"]}
            label="Prag inactivitate (zile)"
            rules={[{ required: true }]}
            extra="Cati zile fara activitate trebuie sa treaca pentru a unfollow. Backend cere min 30 (sa nu fim pripiti). Recomandat 180."
          >
            <InputNumber min={30} max={365} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </>
      );

    case BotTaskType.WelcomeNewUsers:
      return (
        <>
          <Space.Compact block>
            <Form.Item
              name={["config", "interval_minutes"]}
              label="Interval polling (minute)"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra="Cat de des botul verifica daca s-au inregistrat utilizatori noi. 30 = la fiecare jumate de ora."
            >
              <InputNumber min={5} max={1440} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name={["config", "max_per_day"]}
              label="Maxim welcome/zi"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
              extra="Plafon zilnic pentru utilizatori intampinati de acest bot."
            >
              <InputNumber min={1} max={50} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name={["config", "new_user_window_hours"]}
            label="Fereastra utilizatori noi (ore)"
            rules={[{ required: true }]}
            extra="Doar utilizatorii inregistrati in ultimele N ore primesc welcome. Recomandat 24. Maxim acceptat: 168 (1 saptamana)."
          >
            <InputNumber min={1} max={168} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </>
      );

    // ---- Reposts / amplification ----
    case BotTaskType.RepostFreeFeed:
      return (
        <>
          <Space.Compact block>
            <Form.Item
              name={["config", "reposts_per_day_mean"]}
              label="Reposts pe zi (medie)"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra="Numar mediu de redistribuiri zilnice."
            >
              <InputNumber min={0} max={20} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name={["config", "reposts_per_day_stddev"]}
              label="Deviatie"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra="Variatia fata de medie."
            >
              <InputNumber min={0} max={10} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name={["config", "max_per_day"]}
              label="Plafon zilnic"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
              extra="Limita absoluta de reposts/zi."
            >
              <InputNumber min={1} max={20} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name={["config", "min_post_likes"]}
            label="Prag minim aprecieri"
            rules={[{ required: true }]}
            extra="Botul redistribuie doar postari cu cel putin N likes. Filtreaza zgomotul si redistribuie continut deja popular."
          >
            <InputNumber min={0} max={1000} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </>
      );

    case BotTaskType.QuoteRepostFreeFeed:
      return (
        <Space.Compact block>
          <Form.Item
            name={["config", "quotes_per_day_mean"]}
            label="Quote reposts pe zi (medie)"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
            extra="Continut sintetic — tine rar. Backend respinge configuri >5/zi."
          >
            <InputNumber min={0} max={5} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "quotes_per_day_stddev"]}
            label="Deviatie"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
          >
            <InputNumber min={0} max={3} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "max_per_day"]}
            label="Plafon zilnic"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={1} max={5} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Space.Compact>
      );

    // ---- Comments / discoverability ----
    case BotTaskType.LikeRealComments:
      return (
        <Space.Compact block>
          <Form.Item
            name={["config", "actions_per_day_mean"]}
            label="Comment-likes pe zi (medie)"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
            extra="Cate comentarii apreciaza botul in medie pe zi."
          >
            <InputNumber min={0} max={100} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "actions_per_day_stddev"]}
            label="Deviatie"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
          >
            <InputNumber min={0} max={50} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "max_per_day"]}
            label="Plafon zilnic"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={1} max={100} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Space.Compact>
      );

    case BotTaskType.MentionRelevantUser:
      return (
        <Space.Compact block>
          <Form.Item
            name={["config", "mentions_per_day_mean"]}
            label="Mention-uri pe zi (medie)"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
            extra="Fiecare mention trimite push notification — tine plafonul mic. Backend respinge >5/zi."
          >
            <InputNumber min={0} max={5} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "mentions_per_day_stddev"]}
            label="Deviatie"
            rules={[{ required: true }]}
            style={{ flex: 1, marginRight: 8 }}
          >
            <InputNumber min={0} max={3} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name={["config", "max_per_day"]}
            label="Plafon zilnic"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={1} max={5} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Space.Compact>
      );

    // ---- Events ----
    case BotTaskType.ReactEventMessageContextual:
      return (
        <>
          <Space.Compact block>
            <Form.Item
              name={["config", "reactions_per_day_mean"]}
              label="Reactii pe zi (medie)"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
              extra="Total reactii zilnice in toate evenimentele live."
            >
              <InputNumber min={0} max={200} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name={["config", "reactions_per_day_stddev"]}
              label="Deviatie"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <InputNumber min={0} max={100} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name={["config", "max_per_day"]}
              label="Plafon zilnic"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} max={200} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item
            name={["config", "max_per_event_per_hour"]}
            label="Plafon per eveniment / ora"
            rules={[{ required: true }]}
            extra="Limita pentru a nu inunda un singur chat. Backend respinge >30/h (chat-ul ar parea astroturfat). Recomandat 5-15."
          >
            <InputNumber min={1} max={30} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </>
      );

    default:
      return null;
  }
};

function defaultsFor(taskType: BotTaskType): Record<string, unknown> {
  switch (taskType) {
    case BotTaskType.CreateFreePost:
      return {
        posts_per_week_mean: 2,
        posts_per_week_stddev: 1,
        category_ids: [],
      };
    case BotTaskType.LikeFeed:
      return {
        actions_per_day_mean: 8,
        actions_per_day_stddev: 3,
        max_per_day: 30,
      };
    case BotTaskType.CommentFeed:
      return {
        actions_per_day_mean: 2,
        actions_per_day_stddev: 1,
        max_per_day: 5,
      };
    case BotTaskType.ViewFeed:
      return {
        actions_per_day_mean: 30,
        actions_per_day_stddev: 10,
        max_per_day: 100,
      };
    case BotTaskType.ActivityPing:
      return { interval_minutes: 60, jitter_minutes: 15 };
    case BotTaskType.ReadNotifications:
      return { interval_minutes: 30 };

    // ---- Social graph ----
    case BotTaskType.FollowRelevantUsers:
      return {
        follows_per_day_mean: 5,
        follows_per_day_stddev: 2,
        max_per_day: 15,
      };
    case BotTaskType.UnfollowInactiveUsers:
      return {
        interval_minutes: 720, // 12h — cleanup tasks don't need to run often
        max_per_day: 10,
        inactive_threshold_days: 180,
      };
    case BotTaskType.WelcomeNewUsers:
      return {
        interval_minutes: 30,
        max_per_day: 20,
        new_user_window_hours: 24,
      };

    // ---- Reposts / amplification ----
    case BotTaskType.RepostFreeFeed:
      return {
        reposts_per_day_mean: 3,
        reposts_per_day_stddev: 1,
        max_per_day: 8,
        min_post_likes: 5,
      };
    case BotTaskType.QuoteRepostFreeFeed:
      return {
        quotes_per_day_mean: 1,
        quotes_per_day_stddev: 1,
        max_per_day: 2,
      };

    // ---- Comments / discoverability ----
    case BotTaskType.LikeRealComments:
      return {
        actions_per_day_mean: 10,
        actions_per_day_stddev: 4,
        max_per_day: 30,
      };
    case BotTaskType.MentionRelevantUser:
      return {
        mentions_per_day_mean: 1,
        mentions_per_day_stddev: 1,
        max_per_day: 2,
      };

    // ---- Events ----
    case BotTaskType.ReactEventMessageContextual:
      return {
        reactions_per_day_mean: 20,
        reactions_per_day_stddev: 8,
        max_per_day: 80,
        max_per_event_per_hour: 10,
      };

    default:
      return {};
  }
}
